import React, { useContext } from 'react'
import styled from 'styled-components'
import ScrollAnimation from 'react-animate-on-scroll'

// import { Link } from 'gatsby'
import { white } from 'styles/colors'
import { navHeight, navHeightMobile, cover } from 'styles/global'
import * as spacing from 'styles/spacing'
import { media, mediaDown } from 'styles/media'
import { H1, H3, H4, fontWeights, lineHeights } from 'styles/typography'

import Wrapper from 'components/Wrapper'
import Header from 'components/Header'
import Button from 'components/button/Button'
import { NativeAuthConsumer } from 'components/NativeAuth'
import SaasquatchContext from 'contexts/Saasquatch'

const Container = styled.div`
  position: relative;
  text-align: center;
  padding-top: ${navHeightMobile};
  color: ${white};
  overflow: hidden;
  background-color: #d7bf8b;
  background-image: url('${process.env.BASE_PATH}/images/refer/background.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  ${media.medium`
    text-align: left;
    padding-top: ${navHeight};
    margin-bottom: -2rem;
  `};

  ${media.large`
    margin-bottom: -3rem;
  `};
`

const Outer = styled.div`
  padding-top: 1.5rem;
  padding-bottom: 3rem;

  ${media.small`
    padding-top: 2rem;
    padding-bottom: 4rem;
  `};

  ${media.medium`
    padding-top: 2.5rem;
    padding-bottom: 8rem;
  `};

  ${media.large`
    padding-top: 3rem;
    padding-bottom: 10rem;
  `};

  ${media.xLarge`
    padding-top: 4rem;
    padding-bottom: 16rem;
  `};
`

const Inner = styled.div`
  position: static;

  &:before {
    left: -${spacing.large};
    right: -${spacing.large};
    bottom: -3rem;

    ${media.small`
      left: -${spacing.xxLarge};
      right: -${spacing.xxLarge};
      bottom: -4rem;
    `};
  }

  ${media.medium`
    display: flex;
    justify-content: space-between;
  `};

  ${mediaDown.medium`
    position: relative;

    &:before {
      content: "";
      position: absolute;
      background-color: #14191d;
      top: 0;
    }
  `};
`

const Content = styled.div`
  position: static;

  ${mediaDown.medium`
    max-width: 460px;
    margin-left: auto;
    margin-right: auto;
    padding-top: ${spacing.large};
  `};

  ${media.medium`
    padding-left: 55%;
  `};
`

const Heading = styled(H1)`
  color: ${white};
  font-family: 'Trim Poster';
  margin-bottom: 0.25em !important;
  font-size: 32px;
  text-transform: uppercase;

  ${media.small`
    font-size: 40px;
  `};

  ${media.medium`
    font-size: 44px;
    white-space: nowrap;
  `};

  ${media.large`
    font-size: 54px;
    margin-bottom: 0.4em;
  `};

  ${media.xLarge`
    font-size: 60px;
  `};
`

const Subheading = styled(H3)`
  color: ${white};
  margin-bottom: 0.6em !important;

  ${media.large`
    margin-bottom: 1em;
  `};
`

const Description = styled(H4)`
  color: ${white};
  font-weight: ${fontWeights.regular};
  line-height: ${lineHeights.base};
  margin-bottom: ${spacing.large};

  ${media.medium`
    margin-bottom: ${spacing.xLarge};
  `};

  ${media.large`
    margin-bottom: ${spacing.xxLarge};
  `};

  ${media.xLarge`
    margin-bottom: ${spacing.xxxLarge};
  `};
`

const FigureWrap = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 600px;
  left: 50%;
  margin-bottom: -300px;
  transform: translateX(-50%);
  margin-left: -263px;

  ${media.small`
    width: 680px;
    margin-left: -293px;
    margin-bottom: -340px;
  `};

  ${media.medium`
    position: absolute;
    transform: none;
    width: 720px;
    right: 50%;
    margin-top: ${spacing.large};
    margin-bottom: 0;
    margin-left: 0;
    left: auto;
  `};

  ${media.large`
    width: 940px;
    margin-top: ${spacing.xLarge};
  `};

  ${media.xLarge`
    width: 1080px;
  `};

  &:after {
    content: '';
    display: block;
    padding-bottom: 73.7%;
  }
`

const Figure = styled.img`
  object-fit: cover;
  ${cover('absolute')};
`

const Slant = styled.img`
  position: relative;
  pointer-events: none;
  max-height: 320px;
  z-index: 10;
  top: 1px;

  ${media.medium`
    position: absolute;
    top: auto;
    bottom: 0;
    left: 0;
    width: 100%;
    margin-bottom: -1px;
  `};
`

const ReferHeader = () => {
  const { eligible } = useContext(SaasquatchContext)

  return (
    <Container>
      <Header transparent />
      <Outer>
        <FigureWrap
          className="u-hide-medium-up"
          as={ScrollAnimation}
          animateIn="fadeIn"
          offset={0}
          delay={800}
          animateOnce
        >
          <Figure src={`${process.env.BASE_PATH}/images/refer/devices.png`} />
        </FigureWrap>
        <Slant className="u-hide-medium-up" src={`${process.env.BASE_PATH}/images/refer/slant.png`} />
        <Wrapper>
          <Inner>
            <FigureWrap
              className="u-hide-medium-down"
              as={ScrollAnimation}
              animateIn="fadeIn"
              offset={0}
              delay={800}
              animateOnce
            >
              <Figure src={`${process.env.BASE_PATH}/images/refer/devices.png`} />
            </FigureWrap>
            <Content>
              <ScrollAnimation animateIn="fadeInUp" offset={0} delay={200} animateOnce>
                <Heading>
                  Pass on <br className="u-hide-medium-down" />
                  the savings
                </Heading>
              </ScrollAnimation>
              <ScrollAnimation animateIn="fadeInUp" offset={0} delay={400} animateOnce>
                <Subheading>It's a win-win.</Subheading>
              </ScrollAnimation>
              <Description as={ScrollAnimation} animateIn="fadeInUp" offset={0} delay={600} animateOnce>
                Pass on $20 in savings to a family member or friend using the unique codes below. After they make a
                purchase, you will get $20 in savings of your own!
              </Description>
              <ScrollAnimation animateIn="fadeInUp" offset={0} delay={800} animateOnce>
                <NativeAuthConsumer>
                  {({ user }) => {
                    return user && eligible ? (
                      <a href="#savings">
                        <Button type="button" theme="custom" variant="outline" size="large" customColor={white}>
                          Get started
                        </Button>
                      </a>
                    ) : (
                      <a href="#how-it-works">
                        <Button type="button" theme="custom" variant="outline" size="large" customColor={white}>
                          Learn more
                        </Button>
                      </a>
                    )
                  }}
                </NativeAuthConsumer>
              </ScrollAnimation>
            </Content>
          </Inner>
        </Wrapper>
      </Outer>
      <Slant className="u-hide-medium-down" src={`${process.env.BASE_PATH}/images/refer/slant.png`} />
    </Container>
  )
}

export default ReferHeader
